import React, { useEffect, useState } from "react";
import { Button, ColorPicker, Input, Select, Space, Tooltip, message } from "antd";
import {
    AlignCenterOutlined,
    AlignLeftOutlined,
    AlignRightOutlined,
    BoldOutlined,
    ClearOutlined,
    FontSizeOutlined,
    ItalicOutlined,
    LeftSquareOutlined,
    LinkOutlined,
    OrderedListOutlined,
    RedoOutlined,
    RightSquareOutlined,
    StrikethroughOutlined,
    CheckOutlined,
    CloseOutlined,
    UnderlineOutlined,
    UndoOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle as BaseTextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { sanitizeRichTextHtml } from "./utils/sanitizeRichTextHtml";

const { Option } = Select;

const TooltipTrigger = React.forwardRef<HTMLSpanElement, { children: React.ReactNode }>(
    ({ children }, ref) => (
        <span ref={ref} style={{ display: "inline-flex" }}>
            {children}
        </span>
    ),
);

TooltipTrigger.displayName = "TooltipTrigger";

export interface TiptapRichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    fontSizes?: string[];
}

const ALL_FONT_SIZES: string[] = ["8", "10", "12", "14", "16", "18", "24", "32", "64"];

const TextStyle = BaseTextStyle.extend({
    addAttributes() {
        const parentAttrs = (this.parent && this.parent()) || {};
        return {
            ...parentAttrs,
            fontSize: {
                default: null,
                parseHTML: (element) => element.style.fontSize || null,
                renderHTML: (attributes) => {
                    if (!attributes.fontSize) {
                        return {};
                    }
                    return {
                        style: `font-size: ${attributes.fontSize}`,
                    };
                },
            },
        };
    },
});

export const TiptapRichTextEditor: React.FC<TiptapRichTextEditorProps> = ({
    value,
    onChange,
    placeholder,
    disabled,
    fontSizes,
}) => {
    const [, setEditorRenderTick] = useState(0);
    const [isLinkEditorOpen, setIsLinkEditorOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [2, 3],
                },
                link: {
                    openOnClick: true,
                    linkOnPaste: true,
                    autolink: true,
                },
            }),
            TextStyle,
            Color.configure({ types: [TextStyle.name] }),
            Highlight.configure({ multicolor: true }),
            Placeholder.configure({
                placeholder:
                    placeholder ||
                    "Describe what changed and why (customers will see this)",
            }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
        ],
        content: value || "",
        editorProps: {
            attributes: {
                class: "tiptap-editor-content",
            },
            handlePaste(this: any, _view: any, event: any) {
                const html = event.clipboardData?.getData("text/html");
                if (!html) {
                    return false;
                }

                event.preventDefault();

                const cleaned = sanitizeRichTextHtml(html);

                if (this && this.editor) {
                    this.editor.chain().focus().insertContent(cleaned).run();
                    return true;
                }

                return false;
            },
        },
        onUpdate({ editor }) {
            const html = editor.getHTML();
            if (html !== value) {
                onChange(html);
            }
        },
        editable: !disabled,
    });

    useEffect(() => {
        if (!editor) return;

        const forceUpdate = () => {
            setEditorRenderTick((tick: number) => tick + 1);
        };

        editor.on("selectionUpdate", forceUpdate);

        return () => {
            editor.off("selectionUpdate", forceUpdate);
        };
    }, [editor]);

    useEffect(() => {
        if (!editor) return;
        try {
            const current = editor.getHTML();
            if (value && value !== current) {
                editor.commands.setContent(value, { emitUpdate: false });
            }
            if (!value && current !== "<p></p>") {
                editor.commands.clearContent(false);
            }
        } catch (err) {
            console.error("Error syncing TipTap content from value", err);
            editor.commands.clearContent(false);
            message.warning(
                "There was a problem loading existing content. The text area has been reset.",
            );
        }
    }, [value, editor]);

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [editor, disabled]);

    if (!editor) {
        return null;
    }

    const availableFontSizes = fontSizes && fontSizes.length > 0 ? fontSizes : ALL_FONT_SIZES;

    const normalizeHref = (raw: string): string => {
        const trimmed = raw.trim();
        if (/^(https?:\/\/|mailto:)/i.test(trimmed)) {
            return trimmed;
        }
        return `https://${trimmed}`;
    };

    const applyLink = () => {
        if (!editor) return;
        const trimmed = linkUrl.trim();
        const chain = editor.chain().focus().extendMarkRange("link");

        if (!trimmed) {
            chain.unsetLink().run();
        } else {
            const href = normalizeHref(trimmed);
            chain.setLink({ href, target: "_blank" }).run();
        }

        setIsLinkEditorOpen(false);
    };

    const cancelLink = () => {
        setIsLinkEditorOpen(false);
        setLinkUrl("");
    };

    const colorPresets = [
        {
            label: "Recommended",
            colors: [
                "#000000",
                "#595959",
                "#1890ff",
                "#52c41a",
                "#fa8c16",
                "#f5222d",
                "#722ed1",
                "#ffffff",
            ],
        },
    ];

    const applyFontSize = (size: string | null) => {
        if (!editor) return;

        const currentAttrs = editor.getAttributes("textStyle") || {};
        const chain = editor.chain().focus();

        if (size) {
            chain.setMark("textStyle", { ...currentAttrs, fontSize: size }).run();
        } else {
            const { fontSize, ...rest } = currentAttrs as { fontSize?: string };
            chain.setMark("textStyle", rest).run();
        }
    };

    const applyTextColor = (color: string | null) => {
        if (!editor) return;
        const chain = editor.chain().focus();
        if (color) {
            chain.setColor(color).run();
        } else {
            chain.unsetColor().run();
        }
    };

    const applyBackgroundColor = (color: string | null) => {
        if (!editor) return;
        const chain = editor.chain().focus();
        if (color) {
            chain.setHighlight({ color }).run();
        } else {
            chain.unsetHighlight().run();
        }
    };

    return (
        <div
            style={{
                border: "1px solid #d9d9d9",
                borderRadius: 4,
                padding: 8,
                transition: "border-color 0.2s, box-shadow 0.2s",
                boxShadow: "none",
                backgroundColor: disabled ? "#fafafa" : "#ffffff",
            }}
        >
            {!disabled && (
                <Space size={4} style={{ marginBottom: 8, flexWrap: "wrap" }}>
                    <Select
                        size="small"
                        style={{ width: 110 }}
                        value={(() => {
                            const markSize = editor.getAttributes("textStyle")
                                .fontSize as string | undefined;
                            let current: string = "default";
                            switch (markSize) {
                                case "8pt":
                                    current = "8";
                                    break;
                                case "10pt":
                                    current = "10";
                                    break;
                                case "12pt":
                                    current = "12";
                                    break;
                                case "14pt":
                                    current = "14";
                                    break;
                                case "16pt":
                                    current = "16";
                                    break;
                                case "18pt":
                                    current = "18";
                                    break;
                                case "24pt":
                                    current = "24";
                                    break;
                                case "32pt":
                                    current = "32";
                                    break;
                                case "64pt":
                                    current = "64";
                                    break;
                                default:
                                    current = "default";
                            }
                            return availableFontSizes.includes(current) ? current : "default";
                        })()}
                        suffixIcon={<FontSizeOutlined />}
                        onChange={(val: string) => {
                            if (val === "default") {
                                applyFontSize(null);
                            } else {
                                applyFontSize(`${val}pt`);
                            }
                        }}
                    >
                        <Option value="default">Size</Option>
                        {availableFontSizes.map((size) => (
                            <Option key={size} value={size}>{size} pt</Option>
                        ))}
                    </Select>

                    <Tooltip title="Font color">
                        <TooltipTrigger>
                            <ColorPicker
                                size="small"
                                presets={colorPresets}
                                value={
                                    (editor.getAttributes("textStyle").color as
                                        | string
                                        | undefined) || undefined
                                }
                                onChange={(_, hex) => {
                                    if (!hex) {
                                        applyTextColor(null);
                                    } else {
                                        applyTextColor(hex);
                                    }
                                }}
                            />
                        </TooltipTrigger>
                    </Tooltip>

                    <Tooltip title="Background color">
                        <TooltipTrigger>
                            <ColorPicker
                                size="small"
                                presets={colorPresets}
                                value={
                                    (editor.getAttributes("highlight").color as
                                        | string
                                        | undefined) || undefined
                                }
                                onChange={(_, hex) => {
                                    if (!hex) {
                                        applyBackgroundColor(null);
                                    } else {
                                        applyBackgroundColor(hex);
                                    }
                                }}
                            />
                        </TooltipTrigger>
                    </Tooltip>

                    <span style={{ padding: "0 4px", color: "#999" }}>|</span>

                    <Tooltip title="Bold">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Bold"
                                aria-pressed={editor.isActive("bold")}
                                type={editor.isActive("bold") ? "primary" : "default"}
                                onClick={() => editor.chain().focus().toggleBold().run()}
                            >
                                <BoldOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Italic">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Italic"
                                aria-pressed={editor.isActive("italic")}
                                type={editor.isActive("italic") ? "primary" : "default"}
                                onClick={() => editor.chain().focus().toggleItalic().run()}
                            >
                                <ItalicOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Underline">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Underline"
                                aria-pressed={editor.isActive("underline")}
                                type={editor.isActive("underline") ? "primary" : "default"}
                                onClick={() => editor.chain().focus().toggleUnderline().run()}
                            >
                                <UnderlineOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Strikethrough">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Strikethrough"
                                aria-pressed={editor.isActive("strike")}
                                type={editor.isActive("strike") ? "primary" : "default"}
                                onClick={() => editor.chain().focus().toggleStrike().run()}
                            >
                                <StrikethroughOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>

                    <span style={{ padding: "0 4px", color: "#999" }}>|</span>

                    <Tooltip title="Insert link">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Insert link"
                                onClick={() => {
                                    const previousUrl = editor.getAttributes("link")
                                        .href as string | undefined;
                                    setLinkUrl(previousUrl || "");
                                    setIsLinkEditorOpen(true);
                                }}
                                type={editor.isActive("link") ? "primary" : "default"}
                                disabled={editor.state.selection.empty}
                            >
                                <LinkOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Numbered list">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Numbered list"
                                aria-pressed={editor.isActive("orderedList")}
                                type={
                                    editor.isActive("orderedList") ? "primary" : "default"
                                }
                                onClick={() =>
                                    editor.chain().focus().toggleOrderedList().run()
                                }
                            >
                                <OrderedListOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Bullet list">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Bullet list"
                                aria-pressed={editor.isActive("bulletList")}
                                type={
                                    editor.isActive("bulletList") ? "primary" : "default"
                                }
                                onClick={() =>
                                    editor.chain().focus().toggleBulletList().run()
                                }
                            >
                                <UnorderedListOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Blockquote">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Blockquote"
                                aria-pressed={editor.isActive("blockquote")}
                                type={
                                    editor.isActive("blockquote") ? "primary" : "default"
                                }
                                onClick={() =>
                                    editor.chain().focus().toggleBlockquote().run()
                                }
                            >
                                "+"
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>

                    <span style={{ padding: "0 4px", color: "#999" }}>|</span>

                    <Tooltip title="Align left">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Align left"
                                aria-pressed={editor.isActive({ textAlign: "left" })}
                                type={
                                    editor.isActive({ textAlign: "left" })
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() =>
                                    editor.chain().focus().setTextAlign("left").run()
                                }
                            >
                                <AlignLeftOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Align center">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Align center"
                                aria-pressed={editor.isActive({ textAlign: "center" })}
                                type={
                                    editor.isActive({ textAlign: "center" })
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() =>
                                    editor.chain().focus().setTextAlign("center").run()
                                }
                            >
                                <AlignCenterOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Align right">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Align right"
                                aria-pressed={editor.isActive({ textAlign: "right" })}
                                type={
                                    editor.isActive({ textAlign: "right" })
                                        ? "primary"
                                        : "default"
                                }
                                onClick={() =>
                                    editor.chain().focus().setTextAlign("right").run()
                                }
                            >
                                <AlignRightOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Decrease indent">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Decrease indent"
                                onClick={() =>
                                    editor.chain().focus().liftListItem("listItem").run()
                                }
                                disabled={!editor.can().liftListItem("listItem")}
                            >
                                <LeftSquareOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Increase indent">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Increase indent"
                                onClick={() =>
                                    editor.chain().focus().sinkListItem("listItem").run()
                                }
                                disabled={!editor.can().sinkListItem("listItem")}
                            >
                                <RightSquareOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>

                    <span style={{ padding: "0 4px", color: "#999" }}>|</span>
                    <Tooltip title="Undo">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Undo"
                                onClick={() => editor.chain().focus().undo().run()}
                                disabled={!editor.can().undo()}
                            >
                                <UndoOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Redo">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Redo"
                                onClick={() => editor.chain().focus().redo().run()}
                                disabled={!editor.can().redo()}
                            >
                                <RedoOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                    <Tooltip title="Clear formatting and content">
                        <TooltipTrigger>
                            <Button
                                size="small"
                                aria-label="Clear formatting and content"
                                onClick={() =>
                                    editor
                                        .chain()
                                        .focus()
                                        .unsetAllMarks()
                                        .clearNodes()
                                        .run()
                                }
                            >
                                <ClearOutlined />
                            </Button>
                        </TooltipTrigger>
                    </Tooltip>
                </Space>
            )}
            {!disabled && isLinkEditorOpen && (
                <div
                    style={{
                        marginBottom: 8,
                        marginTop: 4,
                        display: "flex",
                        gap: 4,
                        alignItems: "center",
                    }}
                >
                    <Input
                        size="small"
                        placeholder="https://example.com"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onPressEnter={applyLink}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                e.preventDefault();
                                cancelLink();
                            }
                        }}
                        style={{ maxWidth: 260 }}
                    />
                    <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={applyLink}
                    />
                    <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={cancelLink}
                    />
                    <Button
                        size="small"
                        danger
                        onClick={() => {
                            editor
                                .chain()
                                .focus()
                                .extendMarkRange("link")
                                .unsetLink()
                                .run();
                            setIsLinkEditorOpen(false);
                            setLinkUrl("");
                        }}
                    >
                        Remove
                    </Button>
                </div>
            )}
            <div
                style={{
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: 8,
                    minHeight: 120,
                    maxHeight: 220,
                    overflowY: "auto",
                    lineHeight: 1.5,
                    cursor: disabled ? "default" : "text",
                }}
                onMouseDown={(e) => {
                    if (disabled) return;
                    if (e.target !== e.currentTarget) {
                        return;
                    }
                    e.preventDefault();
                    editor.commands.focus("end");
                }}
            >
                <EditorContent
                    editor={editor}
                    style={{ outline: "none" }}
                />
            </div>
        </div>
    );
};

export default TiptapRichTextEditor;
